import React, { useEffect, useMemo, useState } from 'react';
import { HbrDomTable, HbrPagination } from '@harbor/elements-react-standalone';
import { useNavigate } from 'react-router-dom';
import { moodleFetch } from '../../../hooks/useMoodleHooks';
import { buildCourseDetailPath } from '../../../support/moodleDetailRoutes';

const PAGE_SIZE_OPTIONS = [30, 50, 100];
const LEADER_RETURN_TO = '/leader';

type SortKey =
  | 'coverage_desc'
  | 'coverage_asc'
  | 'completed_desc'
  | 'completed_asc'
  | 'course_asc'
  | 'course_desc';

type PillTone = 'success' | 'info' | 'neutral' | 'warning';

interface Props {
    teamEmails: string[];
    onSelectPerson: (email: string) => void;
}

type CompletionRow = {
    course_id: number;
    course_fullname?: string;
    course_shortname?: string;
    category_name?: string;
    completion_status?: string;
    completion_percent?: number;
    is_completed?: boolean;
};

type Row = {
    id: string;
    course_id: number;
    course_fullname: string;
    completed_count: number;
    in_progress_count: number;
    not_started_count: number;
    team_count: number;
    completion_rate: number;
};

function safeId(courseId: unknown) {
    return `ttt_${String(courseId).replace(/[^a-z0-9_-]/gi, '_')}`;
}

function pillStyle(kind: PillTone): React.CSSProperties {
    const map: Record<PillTone, React.CSSProperties> = {
        success: {
            background: 'var(--hbr-color-success-bg, #e8f5e9)',
            border: '1px solid var(--hbr-color-success-border, #a5d6a7)',
            color: 'var(--hbr-color-success-foreground, #1b5e20)',
        },
        info: {
            background: 'var(--hbr-color-info-bg, #e3f2fd)',
            border: '1px solid var(--hbr-color-info-border, #90caf9)',
            color: 'var(--hbr-color-info-foreground, #0d47a1)',
        },
        neutral: {
            background: 'var(--hbr-color-neutral-bg-medium, #f3f4f6)',
            border: '1px solid var(--hbr-color-neutral-border-weak, #d1d5db)',
            color: 'var(--hbr-color-text-primary, #111827)',
        },
        warning: {
            background: 'var(--hbr-color-warning-bg, #fff8e1)',
            border: '1px solid var(--hbr-color-warning-border, #ffe082)',
            color: 'var(--hbr-color-warning-foreground, #8d6e63)',
        },
    };

    return {
        ...map[kind],
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: 999,
        padding: '2px 10px',
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1.5,
        whiteSpace: 'nowrap',
    };
}

function metricCard(label: string, value: React.ReactNode, sublabel?: React.ReactNode) {
    return (
      <div
        className="hbr-css__container-card"
        style={{
            padding: 12,
            minHeight: 84,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 4,
        }}
      >
          <div className="hbr-css__text-secondary-xs">{label}</div>
          <div className="hbr-css__text-primary-md-semibold">{value}</div>
          {sublabel ? <div className="hbr-css__text-secondary-xs">{sublabel}</div> : null}
      </div>
    );
}

function normalizePercent(value: unknown, status: string): number {
    const n = Number(value);
    if (Number.isFinite(n)) return Math.max(0, Math.min(100, n));

    const s = String(status ?? '').trim().toLowerCase();
    if (s === 'completed') return 100;
    if (s === 'in progress') return 50;
    return 0;
}

function getProgressState(percent: number): 'completed' | 'in_progress' | 'not_started' {
    if (percent >= 100) return 'completed';
    if (percent > 0) return 'in_progress';
    return 'not_started';
}

function readinessForRate(rate: number): { label: string; tone: PillTone } {
    if (rate >= 100) return { label: 'Fully Completed', tone: 'success' };
    if (rate >= 75) return { label: 'Strong Completion', tone: 'success' };
    if (rate > 0) return { label: 'Partially Completed', tone: 'info' };
    return { label: 'Not Started', tone: 'warning' };
}

function normalizeResults(data: any): CompletionRow[] {
    if (Array.isArray(data?.results)) return data.results;
    if (Array.isArray(data)) return data;
    return [];
}

async function fetchCourseCompletionsByUser(email: string): Promise<CompletionRow[]> {
    const data = await moodleFetch<any>('moodle/course/completions/by-user/', { email });
    return normalizeResults(data);
}

export default function WorkshopsTeamTttProctorsTable({ teamEmails, onSelectPerson }: Props) {
    const navigate = useNavigate();
    void onSelectPerson;

    const uniqueEmails = useMemo(
      () =>
        Array.from(
          new Set(teamEmails.map((e) => String(e || '').trim().toLowerCase()).filter(Boolean))
        ),
      [teamEmails]
    );

    const [rawByEmail, setRawByEmail] = useState<Record<string, CompletionRow[]>>({});
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        let cancelled = false;

        async function run() {
            if (!uniqueEmails.length) {
                setRawByEmail({});
                setIsLoading(false);
                setError(null);
                return;
            }

            setIsLoading(true);
            setError(null);

            try {
                const results = await Promise.all(
                  uniqueEmails.map(async (email) => {
                      const rows = await fetchCourseCompletionsByUser(email);
                      return [email, rows] as const;
                  })
                );

                if (cancelled) return;

                const next: Record<string, CompletionRow[]> = {};
                for (const [email, rows] of results) {
                    next[email] = rows;
                }
                setRawByEmail(next);
            } catch (err: any) {
                if (!cancelled) {
                    setError(err instanceof Error ? err : new Error(String(err)));
                    setRawByEmail({});
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        }

        run();

        return () => {
            cancelled = true;
        };
    }, [uniqueEmails]);

    const allRows: Row[] = useMemo(() => {
        const teamCount = uniqueEmails.length || 0;

        const allTttCourses = new Map<number, string>();
        const perEmailByCourse = new Map<string, Map<number, 'completed' | 'in_progress' | 'not_started'>>();

        uniqueEmails.forEach((email) => {
            const rows = rawByEmail[email] ?? [];
            const courseStateMap = new Map<number, 'completed' | 'in_progress' | 'not_started'>();

            rows.forEach((r) => {
                const category = String(r.category_name ?? '').trim().toLowerCase();
                if (category !== 'train the trainer') return;

                const cid = Number(r.course_id);
                if (!Number.isFinite(cid)) return;

                const name = String(r.course_fullname ?? r.course_shortname ?? `Course ${cid}`);
                const percent = normalizePercent(r.completion_percent, String(r.completion_status ?? ''));
                const progressState = getProgressState(percent);

                allTttCourses.set(cid, name);
                courseStateMap.set(cid, progressState);
            });

            perEmailByCourse.set(email, courseStateMap);
        });

        return Array.from(allTttCourses.entries()).map(([course_id, course_fullname]) => {
            let completed_count = 0;
            let in_progress_count = 0;
            let not_started_count = 0;

            uniqueEmails.forEach((email) => {
                const state = perEmailByCourse.get(email)?.get(course_id) ?? 'not_started';

                if (state === 'completed') completed_count += 1;
                else if (state === 'in_progress') in_progress_count += 1;
                else not_started_count += 1;
            });

            const completion_rate =
              teamCount > 0 ? Math.round((completed_count / teamCount) * 100) : 0;

            return {
                id: safeId(course_id),
                course_id,
                course_fullname,
                completed_count,
                in_progress_count,
                not_started_count,
                team_count: teamCount,
                completion_rate,
            };
        });
    }, [rawByEmail, uniqueEmails]);

    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(30);
    const [sortBy, setSortBy] = useState<SortKey>('coverage_desc');
    const [minCompleted, setMinCompleted] = useState<number>(0);
    const [courseFilterId, setCourseFilterId] = useState<string>('');

    const courseOptions = useMemo(() => {
        return [...allRows]
          .sort((a, b) => a.course_id - b.course_id)
          .map((r) => ({ id: r.course_id, label: r.course_fullname }));
    }, [allRows]);

    const summary = useMemo(() => {
        const totalCourses = allRows.length;
        const teamSize = uniqueEmails.length;
        const fullyCovered = allRows.filter((r) => r.completion_rate === 100).length;
        const partialCoverage = allRows.filter(
          (r) => r.completion_rate > 0 && r.completion_rate < 100
        ).length;
        const noCoverage = allRows.filter((r) => r.completion_rate === 0).length;
        const avgCoverage = allRows.length
          ? Math.round(allRows.reduce((sum, r) => sum + r.completion_rate, 0) / allRows.length)
          : 0;

        return {
            totalCourses,
            teamSize,
            fullyCovered,
            partialCoverage,
            noCoverage,
            avgCoverage,
        };
    }, [allRows, uniqueEmails.length]);

    const filteredSorted = useMemo(() => {
        let arr = [...allRows];

        if (courseFilterId) {
            const cid = Number(courseFilterId);
            arr = arr.filter((r) => r.course_id === cid);
        }

        arr = arr.filter((r) => r.completed_count >= minCompleted);

        arr.sort((a, b) => {
            if (sortBy === 'coverage_desc') return b.completion_rate - a.completion_rate;
            if (sortBy === 'coverage_asc') return a.completion_rate - b.completion_rate;
            if (sortBy === 'completed_desc') return b.completed_count - a.completed_count;
            if (sortBy === 'completed_asc') return a.completed_count - b.completed_count;
            if (sortBy === 'course_asc') return a.course_fullname.localeCompare(b.course_fullname);
            if (sortBy === 'course_desc') return b.course_fullname.localeCompare(a.course_fullname);
            return 0;
        });

        return arr;
    }, [allRows, courseFilterId, minCompleted, sortBy]);

    const paged = useMemo(() => {
        const start = pageIndex * pageSize;
        return filteredSorted.slice(start, start + pageSize);
    }, [filteredSorted, pageIndex, pageSize]);

    if (isLoading) {
        return <div className="hbr-css__text-secondary-sm">Loading Train-the-Trainer completion…</div>;
    }

    if (error) {
        return <div style={{ color: 'red' }}>{error.message}</div>;
    }

    if (!uniqueEmails.length) {
        return <div className="hbr-css__text-secondary-sm">No team emails found.</div>;
    }

    if (!allRows.length) {
        return (
          <div className="hbr-css__text-secondary-sm">
              No Train-the-Trainer courses found for this team.
          </div>
        );
    }

    return (
      <div className="flex flex-col gap-3 h-full min-h-0">
          <div
            className="grid gap-3"
            style={{ gridTemplateColumns: 'repeat(6, minmax(0, 1fr))' }}
          >
              {metricCard('Team Size', summary.teamSize)}
              {metricCard('TTT Courses', summary.totalCourses)}
              {metricCard('Fully Completed', summary.fullyCovered)}
              {metricCard('Partially Completed', summary.partialCoverage)}
              {metricCard('No Completion', summary.noCoverage)}
              {metricCard('Average Completion', `${summary.avgCoverage}%`)}
          </div>

          <div className="flex gap-2 flex-wrap items-center">
              <select
                className="border rounded px-2 py-1 text-sm"
                value={courseFilterId}
                onChange={(e) => {
                    setCourseFilterId(e.target.value);
                    setPageIndex(0);
                }}
              >
                  <option value="">All TTT Courses</option>
                  {courseOptions.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                        {c.label} ({c.id})
                    </option>
                  ))}
              </select>

              <select
                className="border rounded px-2 py-1 text-sm"
                value={String(minCompleted)}
                onChange={(e) => {
                    setMinCompleted(Number(e.target.value));
                    setPageIndex(0);
                }}
              >
                  <option value="0">Completed ≥ 0</option>
                  <option value="1">Completed ≥ 1</option>
                  <option value="2">Completed ≥ 2</option>
                  <option value="3">Completed ≥ 3</option>
                  <option value="5">Completed ≥ 5</option>
              </select>

              <select
                className="border rounded px-2 py-1 text-sm"
                value={sortBy}
                onChange={(e) => {
                    setSortBy(e.target.value as SortKey);
                    setPageIndex(0);
                }}
              >
                  <option value="coverage_desc">Sort: Completion Coverage (high→low)</option>
                  <option value="coverage_asc">Sort: Completion Coverage (low→high)</option>
                  <option value="completed_desc">Sort: Completed Count (high→low)</option>
                  <option value="completed_asc">Sort: Completed Count (low→high)</option>
                  <option value="course_asc">Sort: Course (A→Z)</option>
                  <option value="course_desc">Sort: Course (Z→A)</option>
              </select>
          </div>

          <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <HbrDomTable
                autoLayout
                style={{ display: 'block', width: '100%' }}
                columns={[
                    { id: 'course', name: 'Train the Trainer Course', width: '34%' },
                    { id: 'completed_count', name: 'Completed', width: '11%' },
                    { id: 'in_progress_count', name: 'In Progress', width: '11%' },
                    { id: 'not_started_count', name: 'Not Started', width: '11%' },
                    { id: 'team_count', name: 'Team Size', width: '11%' },
                    { id: 'completion_rate', name: 'Completion %', width: '11%' },
                    { id: 'readiness', name: 'Status', width: '11%' },
                ] as any}
                data={paged as any}
              >
                  {paged.flatMap((r) => {
                      const href = buildCourseDetailPath(r.course_id, { returnTo: LEADER_RETURN_TO });
                      const readiness = readinessForRate(r.completion_rate);

                      return [
                          <div
                            key={`c-${r.id}`}
                            slot={`row-${r.id}-column-course`}
                            className="min-h-10 min-w-0 py-2 flex items-center font-semibold"
                            style={{ whiteSpace: 'normal', lineHeight: 1.35 }}
                          >
                              {href ? (
                                <button
                                  className="bg-transparent border-none p-0 m-0 text-link underline cursor-pointer font-inherit text-left"
                                  type="button"
                                  onClick={() => navigate(href)}
                                  title={r.course_fullname}
                                  style={{ whiteSpace: 'normal', lineHeight: 1.35 }}
                                >
                                    {r.course_fullname}
                                </button>
                              ) : (
                                r.course_fullname
                              )}
                          </div>,

                          <div
                            key={`done-${r.id}`}
                            slot={`row-${r.id}-column-completed_count`}
                            className="min-h-10 py-2 flex items-center justify-end font-semibold"
                          >
                              {r.completed_count}
                          </div>,

                          <div
                            key={`prog-${r.id}`}
                            slot={`row-${r.id}-column-in_progress_count`}
                            className="min-h-10 py-2 flex items-center justify-end"
                          >
                              {r.in_progress_count}
                          </div>,

                          <div
                            key={`ns-${r.id}`}
                            slot={`row-${r.id}-column-not_started_count`}
                            className="min-h-10 py-2 flex items-center justify-end"
                          >
                              {r.not_started_count}
                          </div>,

                          <div
                            key={`team-${r.id}`}
                            slot={`row-${r.id}-column-team_count`}
                            className="min-h-10 py-2 flex items-center justify-end"
                          >
                              {r.team_count}
                          </div>,

                          <div
                            key={`cov-${r.id}`}
                            slot={`row-${r.id}-column-completion_rate`}
                            className="min-h-10 py-2 flex items-center justify-end font-semibold"
                          >
                              {r.completion_rate}%
                          </div>,

                          <div
                            key={`rd-${r.id}`}
                            slot={`row-${r.id}-column-readiness`}
                            className="min-h-10 py-2 flex items-center"
                          >
                              <span style={pillStyle(readiness.tone)}>{readiness.label}</span>
                          </div>,
                      ];
                  })}
              </HbrDomTable>
          </div>

          <HbrPagination
            itemCount={filteredSorted.length}
            pageSize={pageSize}
            page={pageIndex}
            advanced
            pageInfo
            sizeChanger
            pageOptions={PAGE_SIZE_OPTIONS}
            onHbr-size-changed={(e) => {
                setPageSize(e.detail.pageSize);
                setPageIndex(0);
            }}
            onHbr-page-changed={(e) => setPageIndex(e.detail)}
          />
      </div>
    );
}